<template>
    <div id="QrCodeGenerator">
        <TipBox info no-border>
            <span v-html="description"></span> <a :href="url" target="_blank">{{ url }}</a>
        </TipBox>
        <Panel :title="$t('qr_payment.qr_code.title')">
            <div class="columns">
                <div class="column">
                    <div class="group">
                        <ImageUpload :title="$t('qr_payment.qr_code.download_logo')" identifier="logo" :maxSizeInMb="0.8"
                                     :previewHeight="120" :object="logo" @changed="logo = $event"></ImageUpload>
                    </div>
                  <div class="group">
                    <label>{{$t('qr_payment.qr_code.background_color')}}</label>
                    <input type="color" v-model="backgroundColor">
                  </div>
                </div>
                <div class="column">
                  <div class="group">
                    <label>{{$t('qr_payment.qr_code.corner_color')}}</label>
                    <input type="color" v-model="cornerSquareColor">
                  </div>
                  <br>
                    <div class="subtitle">{{$t('qr_payment.qr_code.dot_configuration')}}</div>
                    <div class="group">
                        <label>{{$t('qr_payment.qr_code.dot_type')}}</label>
                        <select v-model="dotType">
                            <option :value="type" v-for="type of dotTypes">{{$t('qr_payment.qr_code.' + type)}}</option>
                        </select>
                    </div>
                    <div class="group">
                        <label>{{$t('qr_payment.qr_code.dot_color')}}</label>
                        <input type="color" v-model="dotColor">
                    </div>
                  <template v-if="urls && urls.length > 0">
                    <br>
                    <div class="subtitle">{{$t('settings.advanced_configurations')}}</div>
                    <div class="group">
                      <input :id="'chkFileNameCanvas' + uuid" type="checkbox" v-model="showFileNameOnCanvas">
                      <label id="label__chkFileNameCanvas" :for="'chkFileNameCanvas' + uuid">{{$t('qr_payment.qr_code.add_file_name_to_img')}}</label>
                    </div>
                  </template>
                </div>
            </div>
            <div class="buttons">
                <button class="btn btn-primary" v-if="url" @click="generateQRCode">{{ $t('qr_payment.qr_code.generate')}}</button>
                <button class="btn btn-primary" v-if="urls && urls.length > 0" @click="generateMultipleQRCodes">{{ $t('qr_payment.qr_code.generate_table')}}</button>
                <div class="grow"></div>
                <button class="btn btn-primary" v-if="qrCodes.length > 0" @click="downloadAllQRCodes">{{$t('qr_payment.qr_code.download_all')}}</button>
            </div>
            <div id="canvas"></div>
            <div class="download-button" v-if="qrCode">
                <br>
                <button class="btn btn-primary" v-if="qrCode" @click="downloadQRCode">{{$t('qr_payment.download')}}</button>
            </div>
            <div class="multiple-canvas" v-if="urls">
                <div class="canvas-holder" v-for="u of urls" :key="'canvas-' + u.filename">
                    <div :id="'canvas-' + u.filename"></div>
                    <div class="filename" v-if="!u.showFileNameOnCanvas && qrCodes.find(q => q.filename === u.filename)">{{u.filename}}</div>
                </div>
            </div>
        </Panel>
    </div>
</template>

<script>
  import QRCodeStyling from "qr-code-styling";
  import JSZip from "jszip";
  import ImageUpload from '@/components/tools/ImageUpload.vue';

  export default {
    name: "QrCodeGenerator",

    props: {
      url: String,
      urls: Array,
      description: String
    },

    components: { ImageUpload },

    data() {
      return {
        uuid: null,
        logo: {url: ""},
        showLogo: true,
        showFileNameOnCanvas: false,
        dotColor: "#000000",
        dotType: "rounded",
        dotTypes: ["rounded", "dots", "square", "extra-rounded"],
        backgroundColor: "#ffffff",
        cornerSquareColor: "#000000",
        qrCode: null,
        qrCodes: []
      }
    },

    mounted() {
      this.uuid = window.generateUUID();
    },

    methods: {
      getDimension() {
        return this.$mq === 'xs' || this.$mq === 'sm' ? 250 : 550;
      },
      getQRCodeOptions(url) {
        let dimension = this.getDimension();
        return {
          width: dimension,
          height: dimension,
          image: this.logo.url && this.showLogo ? this.logo.url : undefined,
          data: url,
          dotsOptions: {
            color: this.dotColor,
            type: this.dotType
          },
          cornersSquareOptions: {
            color: this.cornerSquareColor
          },
          backgroundOptions: {
            color: this.backgroundColor
          },
          imageOptions: {
            crossOrigin: "anonymous",
            margin: 5
          }
        };
      },
      generateQRCode() {
        let options = this.getQRCodeOptions(this.url);
        if (!this.qrCode) {
          this.qrCode = new QRCodeStyling(options);
          this.qrCode.append(document.getElementById("canvas"));
        } else {
          this.qrCode.update(options);
        }
      },
      addFileNameToCanvas(canvasHolder, fileName) {
        let canvas = canvasHolder.getElementsByTagName("canvas")[0];
        if (canvas) {
          let context = canvas.getContext("2d");
          // Create backup canvas to transfert content (is deleted on resize)
          let backupCanvas = document.createElement("canvas");
          let backupContext = backupCanvas.getContext("2d");
          backupCanvas.height = canvas.height;
          backupCanvas.width = canvas.width;
          // Copy canvas
          backupContext.drawImage(canvas, 0, 0);
          canvas.height = this.getDimension() + 60; // Expand size to add text
          context.drawImage(backupCanvas, 0, 0); // Add back original canvas
          // Add text
          context.font = "bold 20px sans-serif";
          context.fillStyle = "black";
          context.textAlign = "center";
          context.fillText(fileName.toUpperCase(), canvas.width / 2, canvas.height - 30);
        }
      },
      async generateMultipleQRCodes() {
        for (let url of this.urls) {
          let qrCode = this.qrCodes.find(q => q.filename === url.filename);
          let options = this.getQRCodeOptions(url.url);
          let canvas = document.getElementById("canvas-" + url.filename);
          url.showFileNameOnCanvas = this.showFileNameOnCanvas;
          if (!qrCode) {
            let code = new QRCodeStyling(options, true);
            code.append(canvas);
            this.qrCodes.push({
              filename: url.filename,
              code: code
            });
          } else {
            qrCode.code.update(options);
          }
        }
        if (this.showFileNameOnCanvas) {
          // Canvas has to be drawn before you can copy it
          setTimeout(async () => {
            for (let url of this.urls) {
              let canvas = document.getElementById("canvas-" + url.filename);
              if (canvas) {
                this.addFileNameToCanvas(canvas, url.filename);
              }
            }
            this.$forceUpdate();
          }, 300);
        } else {
          this.$forceUpdate();
        }
      },
      downloadQRCode() {
        if (!this.qrCode) {
          return;
        }
        this.qrCode.download();
      },
      async downloadAllQRCodes() {
        showSpinner();
        let zip = new JSZip();

        for (let url of this.urls) {
          let canvasHolder = document.getElementById("canvas-" + url.filename);
          if (canvasHolder) {
            let canvas = canvasHolder.getElementsByTagName("canvas")[0];
            if (canvas) {
              let data = window.getBase64Data(canvas.toDataURL());
              zip.file(url.filename + ".png", data, {base64: true})
            }
          }
        }

        let data = await zip.generateAsync({type: "base64"});
        if (data) {
          window.downloadAsZip("ishopfood-qr-codes", data);
        } else {
          toast(this.$t("error.download_failed"), "error");
        }
        hideSpinner();
      }
    }
  }
</script>

<style lang="scss" scoped>
    #canvas {
        display: flex;
        align-items: center;
        justify-content: center;
        margin-top: 20px;
    }
    .buttons {
        display: flex;
        margin-top: 20px;
        margin-bottom: 40px;
        .btn:not(:last-child) {
            margin-right: 10px;
        }
        .grow {
            flex-grow: 1;
        }
    }
    .download-button {
        display: flex;
        justify-content: center;
        width: 100%;
        margin-top: 20px;
        margin-bottom: 30px;
    }
    .multiple-canvas {
        display: flex;
        flex-wrap: wrap;
        justify-content: space-around;

        .filename {
            text-transform: uppercase;
            padding: 10px 20px 30px 20px;
            font-weight: 900;
            text-align: center;
            letter-spacing: 2px;
        }
    }
</style>
